import React, { useState, useEffect, useCallback } from "react";
import {
  Mail,
  Calendar,
  Link2,
  Unlink,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Settings,
  X,
  MapPin,
  Users,
  Inbox,
  Search,
  ChevronLeft,
  Video,
  ExternalLink,
  Clock,
  Reply,
  Star,
  HelpCircle,
  XCircle,
} from "lucide-react";
import {
  getGoogleOAuthUrl,
  listGoogleAccounts,
  disconnectGoogleAccount,
  listGmailThreads,
  getGmailThread,
  listCalendarEvents,
} from "../api/googleIntegration";
import { fetchClaw9Accounts } from "../api/claw9";

// ==================== HELPERS ====================

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatEventTime = (event) => {
  const start = event.start?.dateTime || event.start?.date;
  const end = event.end?.dateTime || event.end?.date;
  if (!start) return "";

  const startDate = new Date(start);
  const isAllDay = !event.start?.dateTime;

  if (isAllDay) return "All day";

  const timeStr = startDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (end) {
    const endDate = new Date(end);
    const endTimeStr = endDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${timeStr} - ${endTimeStr}`;
  }
  return timeStr;
};

const getEventDateKey = (event) => {
  const start = event.start?.dateTime || event.start?.date;
  if (!start) return "";
  return new Date(start).toDateString();
};

// ==================== GMAIL THREAD ITEM ====================

const GmailThreadItem = ({ thread, onClick, isSelected }) => {
  const snippet = thread.snippet || "";
  const displaySubject = snippet ? snippet.substring(0, 80) : "(No Subject)";

  return (
    <button
      onClick={() => onClick?.(thread)}
      className={`w-full text-left p-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 ${
        isSelected ? "bg-[#ff4d4d]/10 border-l-2 border-l-[#ff4d4d]" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#ff4d4d]/20 to-orange-500/20 flex items-center justify-center">
          <Mail className="w-4 h-4 text-[#ff4d4d]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white/80 font-medium truncate">
            {displaySubject}
          </p>
          <p className="text-xs text-white/40 truncate mt-1">
            {snippet.substring(0, 100)}
          </p>
        </div>
      </div>
    </button>
  );
};

// ==================== EMAIL DETAIL VIEW ====================

const EmailDetailView = ({ thread, onBack, accountId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fullThread, setFullThread] = useState(null);

  const previewSnippet = thread.snippet || "";
  const previewSubject = previewSnippet ? previewSnippet.substring(0, 80) : "(No Subject)";

  useEffect(() => {
    const loadFullThread = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const result = await getGmailThread(accountId, thread.id);
        const threadData = result.data?.data || result.data;
        if (result.success && threadData) {
          setFullThread(threadData);
        } else {
          setLoadError(true);
        }
      } catch (err) {
        console.error("Failed to load thread:", err);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadFullThread();
  }, [thread, accountId]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-white truncate flex-1">
            {previewSubject}
          </h2>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#ff4d4d] animate-spin" />
          </div>
        ) : (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#ff4d4d]/20 to-orange-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#ff4d4d]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">Email Preview</p>
                  <p className="text-xs text-white/40">Thread ID: {thread.id?.substring(0, 12)}...</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm text-white/80 whitespace-pre-wrap break-words">
                {thread.snippet || "(No preview available)"}
              </div>
              {loadError && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-xs text-yellow-400">
                    Full email content couldn't be loaded. Showing preview only.
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-white/5 flex gap-2">
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ff4d4d] hover:bg-[#ff3333] text-white text-sm transition-colors">
                <Reply className="w-4 h-4" />
                Reply
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-sm transition-colors">
                <Reply className="w-4 h-4 scale-x-[-1]" />
                Forward
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CALENDAR EVENT CARD ====================

const CalendarEventCard = ({ event }) => {
  const time = formatEventTime(event);
  const isAllDay = !event.start?.dateTime;
  const hasVideoConference = event.hangoutLink || event.conferenceData;

  const getResponseStatus = () => {
    const selfAttendee = event.attendees?.find((a) => a.self);
    return selfAttendee?.responseStatus || null;
  };

  const responseStatus = getResponseStatus();
  const responseConfig = {
    accepted: { icon: CheckCircle, color: "text-emerald-400", label: "Accepted" },
    declined: { icon: XCircle, color: "text-red-400", label: "Declined" },
    tentative: { icon: HelpCircle, color: "text-yellow-400", label: "Maybe" },
    needsAction: { icon: Clock, color: "text-white/40", label: "Pending" },
  };

  return (
    <div className="bg-white/5 rounded-xl border border-white/10 p-4 hover:bg-white/[0.07] transition-colors">
      <div className="flex gap-4">
        <div className="flex-shrink-0 w-20">
          <p className={`text-sm font-medium ${isAllDay ? "text-purple-400" : "text-[#ff4d4d]"}`}>
            {time}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium truncate">
            {event.summary || "(No title)"}
          </h4>
          {event.location && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-white/50">
              <MapPin className="w-3.5 h-3.5" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
          {hasVideoConference && (
            <a
              href={event.hangoutLink || event.conferenceData?.entryPoints?.[0]?.uri}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 text-xs text-blue-400 hover:text-blue-300"
            >
              <Video className="w-3.5 h-3.5" />
              Join Meeting
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {event.attendees?.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-white/50">
              <Users className="w-3.5 h-3.5" />
              <span>{event.attendees.length} attendee{event.attendees.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const GoogleIntegrations = ({ userEmail }) => {
  const [activeTab, setActiveTab] = useState("settings");
  const [accounts, setAccounts] = useState([]);
  const [claw9AccountId, setClaw9AccountId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedScopes, setSelectedScopes] = useState(["gmail", "calendar"]);

  // Gmail state
  const [gmailThreads, setGmailThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [isLoadingGmail, setIsLoadingGmail] = useState(false);
  const [gmailSearchQuery, setGmailSearchQuery] = useState("");

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  // Load Claw9 account
  useEffect(() => {
    const loadClaw9Account = async () => {
      try {
        const result = await fetchClaw9Accounts();
        const accountsList = result.accounts || [];
        if (accountsList.length > 0) {
          setClaw9AccountId(accountsList[0].account_id);
        }
      } catch (err) {
        console.error("Failed to load Claw9 accounts:", err);
      }
    };
    loadClaw9Account();
  }, []);

  // Load connected Google accounts
  const loadAccounts = useCallback(async () => {
    if (!claw9AccountId) return;

    setIsLoading(true);
    setError(null);
    try {
      const result = await listGoogleAccounts(claw9AccountId);
      if (result.success) {
        setAccounts(result.data || []);
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : (result.error?.message || 'Failed to load Google accounts');
        setError(errorMsg);
      }
    } catch (err) {
      setError(err.message || "Failed to load Google accounts");
    } finally {
      setIsLoading(false);
    }
  }, [claw9AccountId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Load Gmail threads
  const loadGmail = useCallback(async () => {
    if (!claw9AccountId) return;

    setIsLoadingGmail(true);
    try {
      const result = await listGmailThreads(claw9AccountId, {
        label: "INBOX",
        max_results: 20,
        query: gmailSearchQuery || undefined,
      });
      if (result.success) {
        const threads = result.data?.data?.threads || result.data?.threads || [];
        setGmailThreads(threads);
      }
    } catch (err) {
      console.error("Failed to load Gmail:", err);
    } finally {
      setIsLoadingGmail(false);
    }
  }, [claw9AccountId, gmailSearchQuery]);

  // Load Calendar events
  const loadCalendar = useCallback(async () => {
    if (!claw9AccountId) return;

    setIsLoadingCalendar(true);
    try {
      const result = await listCalendarEvents(claw9AccountId, { max_results: 30 });
      if (result.success) {
        const events = result.data?.data?.events || result.data?.data?.items || result.data?.events || [];
        setCalendarEvents(events);
      }
    } catch (err) {
      console.error("Failed to load Calendar:", err);
    } finally {
      setIsLoadingCalendar(false);
    }
  }, [claw9AccountId]);

  // Load data when tab changes and account is ready
  useEffect(() => {
    if (claw9AccountId && accounts.length > 0) {
      if (activeTab === "gmail" && gmailThreads.length === 0) {
        loadGmail();
      }
      if (activeTab === "calendar" && calendarEvents.length === 0) {
        loadCalendar();
      }
    }
  }, [activeTab, claw9AccountId, accounts, gmailThreads.length, calendarEvents.length, loadGmail, loadCalendar]);

  // Handle connect
  const handleConnect = async () => {
    if (!claw9AccountId) {
      setError("No Claw9 account found.");
      return;
    }
    if (selectedScopes.length === 0) {
      setError("Please select at least one permission.");
      return;
    }

    setIsConnecting(true);
    setError(null);
    try {
      const result = await getGoogleOAuthUrl({
        accountId: claw9AccountId,
        scopes: selectedScopes,
      });

      if (result.success && result.auth_url) {
        if (result.state) sessionStorage.setItem("google_oauth_state", result.state);
        sessionStorage.setItem("google_oauth_return", window.location.href);
        window.location.href = result.auth_url;
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : (result.error?.message || 'Failed to start Google connection');
        setError(errorMsg);
      }
    } catch (err) {
      setError(err.message || "Failed to start Google connection");
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = async (googleAccountId) => {
    if (!window.confirm("Disconnect this Google account?")) return;

    setDisconnectingId(googleAccountId);
    try {
      const result = await disconnectGoogleAccount(googleAccountId);
      if (result.success) {
        setAccounts(prev => prev.filter(a => a.id !== googleAccountId));
        setGmailThreads([]);
        setCalendarEvents([]);
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : (result.error?.message || 'Failed to disconnect account');
        setError(errorMsg);
      }
    } catch (err) {
      setError(err.message || "Failed to disconnect account");
    } finally {
      setDisconnectingId(null);
    }
  };

  const toggleScope = (scope) => {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  // Group calendar events by date
  const groupEventsByDate = (events) => {
    const today = new Date().toDateString();
    const tomorrow = new Date(Date.now() + 86400000).toDateString();
    const groups = {};

    events.forEach((event) => {
      const dateKey = getEventDateKey(event);
      let label = dateKey;
      if (dateKey === today) label = "Today";
      else if (dateKey === tomorrow) label = "Tomorrow";
      else label = new Date(dateKey).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

      if (!groups[label]) groups[label] = [];
      groups[label].push(event);
    });
    return groups;
  };

  const groupedEvents = groupEventsByDate(calendarEvents);
  const hasGoogleConnected = accounts.length > 0;

  return (
    <div className="h-full flex flex-col bg-[#0a0f1a]">
      {/* Tab Header */}
      <div className="flex-shrink-0 border-b border-white/10">
        <div className="flex">
          <button
            onClick={() => { setActiveTab("settings"); setSelectedThread(null); }}
            className={`flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === "settings"
                ? "text-[#ff4d4d] border-b-2 border-[#ff4d4d] bg-[#ff4d4d]/5"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={() => { setActiveTab("gmail"); setSelectedThread(null); }}
            disabled={!hasGoogleConnected}
            className={`flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              activeTab === "gmail"
                ? "text-[#ff4d4d] border-b-2 border-[#ff4d4d] bg-[#ff4d4d]/5"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <Mail className="w-4 h-4" />
            Gmail
            {gmailThreads.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-white/10">{gmailThreads.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("calendar")}
            disabled={!hasGoogleConnected}
            className={`flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              activeTab === "calendar"
                ? "text-[#ff4d4d] border-b-2 border-[#ff4d4d] bg-[#ff4d4d]/5"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <Calendar className="w-4 h-4" />
            Calendar
            {calendarEvents.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-white/10">{calendarEvents.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="h-full overflow-auto p-6">
            <div className="max-w-2xl">
              <h1 className="text-xl font-semibold text-white mb-2">Google Integration</h1>
              <p className="text-sm text-white/50 mb-6">
                Connect your Google account to access Gmail and Calendar through Claw9
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                  <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
                </div>
              )}

              {/* Connect Card */}
              <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/10 mb-6">
                <h2 className="text-lg font-medium text-white mb-2">Connect Google Account</h2>
                <p className="text-sm text-white/60 mb-4">
                  Grant Claw9 access to your Gmail and Calendar. Your data stays secure.
                </p>

                <div className="flex flex-wrap gap-3 mb-4">
                  <button
                    onClick={() => toggleScope("gmail")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                      selectedScopes.includes("gmail")
                        ? "bg-blue-500/15 border-blue-500/30 text-blue-400"
                        : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Gmail Access</span>
                    {selectedScopes.includes("gmail") && <CheckCircle className="w-4 h-4 ml-1" />}
                  </button>
                  <button
                    onClick={() => toggleScope("calendar")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                      selectedScopes.includes("calendar")
                        ? "bg-green-500/15 border-green-500/30 text-green-400"
                        : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">Calendar Access</span>
                    {selectedScopes.includes("calendar") && <CheckCircle className="w-4 h-4 ml-1" />}
                  </button>
                </div>

                <button
                  onClick={handleConnect}
                  disabled={isConnecting || selectedScopes.length === 0}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] hover:shadow-lg hover:shadow-[#ff4d4d]/30 text-white font-medium transition-all disabled:opacity-50"
                >
                  {isConnecting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Connecting...</>
                  ) : (
                    <><Link2 className="w-5 h-5" />Connect with Google</>
                  )}
                </button>
              </div>

              {/* Connected Accounts */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-[#ff4d4d] animate-spin" />
                </div>
              ) : accounts.length > 0 && (
                <div>
                  <h2 className="text-lg font-medium text-white mb-4">Connected Accounts</h2>
                  {accounts.map((account) => (
                    <div key={account.id} className="p-4 rounded-xl bg-[#0d1424] border border-white/10 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-yellow-500 flex items-center justify-center">
                            <span className="text-white font-bold">G</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{account.email}</p>
                            <p className="text-xs text-emerald-400 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Connected
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDisconnect(account.id)}
                          disabled={disconnectingId === account.id}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm transition-colors"
                        >
                          {disconnectingId === account.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Unlink className="w-4 h-4" />
                          )}
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gmail Tab */}
        {activeTab === "gmail" && (
          <div className="h-full flex flex-col">
            {selectedThread ? (
              <EmailDetailView
                thread={selectedThread}
                onBack={() => setSelectedThread(null)}
                accountId={claw9AccountId}
              />
            ) : (
              <>
                <div className="flex-shrink-0 p-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        type="text"
                        value={gmailSearchQuery}
                        onChange={(e) => setGmailSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && loadGmail()}
                        placeholder="Search emails..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#ff4d4d]/50"
                      />
                    </div>
                    <button
                      onClick={loadGmail}
                      disabled={isLoadingGmail}
                      className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingGmail ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto">
                  {isLoadingGmail ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 text-[#ff4d4d] animate-spin" />
                    </div>
                  ) : gmailThreads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-white/40">
                      <Inbox className="w-12 h-12 mb-3 opacity-50" />
                      <p className="text-lg">No emails found</p>
                    </div>
                  ) : (
                    gmailThreads.map((thread) => (
                      <GmailThreadItem
                        key={thread.id}
                        thread={thread}
                        onClick={setSelectedThread}
                        isSelected={selectedThread?.id === thread.id}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="h-full flex flex-col">
            <div className="flex-shrink-0 p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Upcoming Events</h3>
                <button
                  onClick={loadCalendar}
                  disabled={isLoadingCalendar}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingCalendar ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {isLoadingCalendar ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#ff4d4d] animate-spin" />
                </div>
              ) : calendarEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-white/40">
                  <Calendar className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-lg">No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedEvents).map(([dateLabel, events]) => (
                    <div key={dateLabel}>
                      <h4 className="text-sm font-medium text-white/50 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {dateLabel}
                      </h4>
                      <div className="space-y-3">
                        {events.map((event) => (
                          <CalendarEventCard key={event.id} event={event} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleIntegrations;
